import type { AnalyticDateFilter, GemXQlFilters, GemXQlOverrideFilters } from '../../types';

import { getDateRangeForQuery } from '../helpers/date-query-helpers';
import { dayjsTz } from '~/utils/dayjs';

export interface IBuildTimeQueryParams {
  dateRange?: AnalyticDateFilter;
  compareDateRange?: AnalyticDateFilter;
  overrideFilters?: GemXQlOverrideFilters;
  timeDimension?: string;
}

export type IParseTimeQueryResult = {
  dateRange?: Pick<AnalyticDateFilter, 'startDate' | 'endDate'>;
  compareDateRange?: Pick<AnalyticDateFilter, 'startDate' | 'endDate'>;
  timeDimension?: string;
  overrideFilters?: GemXQlFilters;
};

const buildOverrideTimeQuery = (key: string, value: string) => `DURING (${value}, ${key})`;

const build = (data: IBuildTimeQueryParams) => {
  const { dateRange, compareDateRange, overrideFilters, timeDimension } = data;
  const overrideTimeValue = timeDimension
    ? (overrideFilters as Record<string, string | undefined>)?.[timeDimension]
    : undefined;

  if (timeDimension && overrideTimeValue) {
    return buildOverrideTimeQuery(timeDimension, overrideTimeValue);
  }
  if (!dateRange) return '';

  const { dateGTE, dateLTE } = getDateRangeForQuery(dateRange);
  const { dateGTE: compareDateGTE, dateLTE: compareDateLTE } = compareDateRange
    ? getDateRangeForQuery(compareDateRange)
    : {};
  const compareToClause = compareDateRange
    ? `COMPARE TO ${compareDateGTE} UNTIL ${compareDateLTE}`
    : '';

  return `SINCE ${dateGTE} UNTIL ${dateLTE} ${compareToClause}`;
};

const parse = (query: string): IParseTimeQueryResult => {
  // DURING (value, key) — override time takes precedence
  const duringMatch = query.match(/\bDURING\s+\(([^,]+),\s*([^)]+)\)/i);
  if (duringMatch) {
    const [, value, key] = duringMatch;
    return {
      timeDimension: key.trim(),
      overrideFilters: { [key.trim()]: value.trim() } as GemXQlFilters,
    };
  }

  const sinceMatch = query.match(/\bSINCE\s+(\S+)\s+UNTIL\s+(\S+)/i);
  if (!sinceMatch) return {};

  const dateRange = { startDate: dayjsTz(sinceMatch[1]), endDate: dayjsTz(sinceMatch[2]) };

  const compareMatch = query.match(/\bCOMPARE TO\s+(\S+)\s+UNTIL\s+(\S+)/i);
  const compareDateRange = compareMatch
    ? { startDate: dayjsTz(compareMatch[1]), endDate: dayjsTz(compareMatch[2]) }
    : undefined;

  return { dateRange, compareDateRange };
};

export { build as buildTimeQuery, parse as parseTimeQuery };
